import 'vue-toast-notification/dist/theme-sugar.css'
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import http from '@/services/http.service'
import statementService from '@/services/statement.service'
import type { Module } from "@/types/domain";
import type { ModuleVM } from "@/types/view";
import { buildModuleVM } from "@/lib/buildModuleVM";
import { forEachStatement } from "@/lib/templateWalk";

const path = 'modules'
export const useModuleStore = defineStore('useModuleStore',() => {

    const isLoaded = ref(false)
    const modules = ref<ModuleVM[]>([]);

    // Persist just this primitive
    const currentModuleId = ref<string | null>(null);
    const toId = (x: unknown) => (x == null ? "" : String(x));

    // Derive the object from id + templates
    const currentModule = computed<ModuleVM | null>(() => {
      const id = toId(currentModuleId.value);
      return modules.value.find(t => toId(t.id) === id) as ModuleVM | undefined ?? null;
    });

    function reset() {
      modules.value = []
      isLoaded.value = false
      currentModuleId.value = null
    }

    async function fetchAll(autoSelectFirst = true) {
      isLoaded.value = false;
      const raw = await http.get(path) as Module[]
      const vms = raw.map(buildModuleVM)
      modules.value = vms
      modules.value.sort((a, b) => a.title.localeCompare(b.title))
      if (autoSelectFirst) ensureSelected(null); // pick first if nothing valid yet
      isLoaded.value = true;
    }

    async function persistEvaluation(statementId: string, evaluation: number | null) : Promise<boolean> {
      if (evaluation == null) {
        console.error('Evaluation is null. Aborting')
        return false
      }

      if (!currentModule.value) {
        console.error('Current module is null. Aborting')
        return false
      }

      const moduleId = currentModule.value.id

      return statementService.setEvaluation(moduleId, statementId, evaluation).then(
        () => {
          //Update local storage
          setStatementEvaluation(moduleId, statementId, evaluation)
          return true
        },
        (onReject) => {
          console.error('Failed to save evaluation')
          console.log(JSON.stringify(onReject))
          return false
        }
      )
    }

    function setCurrentModuleFromId(id: string) {
      const idStr = toId(id);
      if (modules.value.some(t => toId(t.id) === idStr)) {
        currentModuleId.value = idStr;
      }
    }

    function setStatementEvaluation(moduleId: string, statementId: string, value: number | null): boolean {
      const t = modules.value.find(tt => tt.id === moduleId) as ModuleVM | undefined;
      if (!t) return false;

      let found = false;
      forEachStatement(t, (s) => {
        if (String(s.id) === String(statementId)) {
          s.evaluation = value;
          found = true;
          return true;
        }
      });

      return found;
    }

    function setStatementRelevant(moduleId: string, statementId: string, relevant: boolean): boolean {
      const t = modules.value.find(tt => tt.id === moduleId) as ModuleVM | undefined;
      if (!t) return false;

      let found = false;
      forEachStatement(t, (s) => {
        if (String(s.id) === String(statementId)) {
          s.relevant = relevant;
          s.evaluation = null; // always clear on toggle
          found = true;
          return true; // stop traversal
        }
      });

      return found;
    }

    function ensureSelected(preferId?: string | null): boolean {
      const wanted = toId(preferId ?? currentModuleId.value);
      const hit = modules.value.find(t => toId(t.id) === wanted) ?? null;
      if (hit) {
        currentModuleId.value = toId(hit.id);
        return true;
      }

      if (modules.value.length > 0) {
        currentModuleId.value = toId(modules.value[0].id);
        return true;
      }
      return false; // no modules loaded
    }

    return {
      fetchAll,
      setCurrentModuleFromId,
      setStatementEvaluation,
      persistEvaluation,
      reset,
      setStatementRelevant,
      ensureSelected,
      modules,
      currentModule,
      isLoaded
    }
  },
  {
    persist: { pick: ['currentModuleId'] },
  }
);


export default useModuleStore
